import { Card, Stack, Badge, Button, Group, ActionIcon } from "@mantine/core";
import { IconPlus, IconEdit, IconTrash } from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import { useInfiniteResource, useRemove } from "../../../shared/api";
import { PageHeader, EmptyState, MoneyDisplay, ResourceForm, confirmDelete, InfiniteTable, type FormField } from "../../../shared/ui";

const FIELDS: FormField[] = [
  { name: "code", label: "Kod", required: true, placeholder: "HAPPY_HOUR, WELCOME10..." },
  { name: "name", label: "Ad", required: true },
  { name: "description", label: "Açıklama", type: "textarea" },
  { name: "discount_type", label: "Tip (PERCENT / FIXED)", required: true, defaultValue: "PERCENT" },
  { name: "scope", label: "Kapsam (ORDER / LINE / CATEGORY)", required: true, defaultValue: "ORDER" },
  { name: "value", label: "Değer", description: "PERCENT için yüzde (örn. 10 = %10). FIXED için tutar TL cinsinden (örn. 50 → 50 ₺ indirim).", type: "number", required: true, min: 0 },
  { name: "max_usage", label: "Max Kullanım (0 = sınırsız)", type: "number", defaultValue: 0, min: 0 },
  { name: "is_active", label: "Aktif", type: "switch", defaultValue: true },
];

export function DiscountsPage() {
  const {
    items: list,
    total,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteResource<any>("discounts");
  const remove = useRemove("discounts");
  const [opened, { open, close }] = useDisclosure(false);
  const [editing, setEditing] = useState<any | null>(null);

  async function handleDelete(id: string) {
    if (!(await confirmDelete("Kampanya silinsin mi?"))) return;
    try {
      await remove.mutateAsync(id);
      notifications.show({ message: "Silindi", color: "green" });
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    }
  }

  return (
    <Stack gap="md">
      <PageHeader
        title="İndirim / Kampanya"
        subtitle={`${total} kural`}
        actions={
          <Button leftSection={<IconPlus size={16} />} onClick={() => { setEditing(null); open(); }}>
            Yeni Kural
          </Button>
        }
      />

      <Card padding="sm" radius="md" withBorder>
        <InfiniteTable
          rows={list}
          rowKey={(d) => d.id}
          isLoading={isLoading}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          emptyContent={
            <EmptyState title="İndirim kuralı yok" description="Happy hour, indirim kodu, kategori indirimi gibi kurallar ekleyin." />
          }
          columns={[
            { header: "Kod", render: (d) => <Badge variant="filled" size="sm">{d.code}</Badge> },
            { header: "Ad", render: (d) => <span style={{ fontWeight: 500 }}>{d.name}</span> },
            { header: "Tip", render: (d) => <Badge variant="light">{d.discount_type}</Badge> },
            { header: "Kapsam", render: (d) => <Badge variant="light" color="gray">{d.scope}</Badge> },
            {
              header: "Değer",
              align: "right",
              render: (d) => (
                <span style={{ fontWeight: 700 }}>
                  {d.discount_type === "PERCENT" ? `%${d.value}` : <MoneyDisplay minor={d.value} />}
                </span>
              ),
            },
            {
              header: "Kullanım",
              render: (d) => `${d.usage_count || 0}${d.max_usage > 0 ? ` / ${d.max_usage}` : ""}`,
            },
            {
              header: "Durum",
              render: (d) =>
                d.is_active ? <Badge color="green">Aktif</Badge> : <Badge color="gray">Pasif</Badge>,
            },
            {
              header: "",
              render: (d) => (
                <Group gap={4}>
                  <ActionIcon variant="subtle" onClick={() => { setEditing(d); open(); }}><IconEdit size={16} /></ActionIcon>
                  <ActionIcon variant="subtle" color="red" onClick={() => handleDelete(d.id)}><IconTrash size={16} /></ActionIcon>
                </Group>
              ),
            },
          ]}
        />
      </Card>

      <ResourceForm
        opened={opened}
        onClose={close}
        resource="discounts"
        title={editing ? "Kural Düzenle" : "Yeni İndirim Kuralı"}
        fields={FIELDS}
        editId={editing?.id || null}
        initialValues={
          editing
            ? {
                ...editing,
                value:
                  editing.discount_type === "FIXED"
                    ? Number(editing.value) / 100
                    : editing.value,
              }
            : undefined
        }
        transform={(values) => ({
          ...values,
          value:
            values.discount_type === "FIXED"
              ? Math.round(Number(values.value) * 100)
              : values.value,
        })}
      />
    </Stack>
  );
}
